import { BreathingSounds } from './BreathingSounds';
import { BreathingTimeEnum, GuidedBreathingModeArray, GuidedBreathingModeEnum } from '../../../types/breathing';
import { GET_BREATHING_SOUNDS, GET_TECHNIQUES } from '../../queries/Technique';
import { Instance, flow, types } from 'mobx-state-tree';
import { Technique } from './Technique';
import { captureException } from '../../../utils/sentry/sentry';
import { clientFunction } from '../../../config/apollo/client';
import { isUndefined } from 'lodash';

export const TechniqueModel = types
  .model('TechniqueModel', {
    techniqueIndex: types.number,
    techniques: types.maybeNull(types.array(Technique)),
    timerDuration: types.number,
    stepVibrationFlag: types.boolean,
    laura: types.maybeNull(BreathingSounds),
    paul: types.maybeNull(BreathingSounds),
    bell: types.maybeNull(BreathingSounds),
    guidedBreathingMode: types.enumeration<string>(GuidedBreathingModeArray),
    state: types.maybe(types.enumeration(['pending', 'done', 'error']))
  })
  .actions(self => {
    const fetchTechniques = flow(function* (language: string) {
      self.state = 'pending';
      try {
        const res = yield clientFunction(language).query({ query: GET_TECHNIQUES });
        if (res) {
          self.techniques = res.data.techniques.items;
        }
        self.state = 'done';
      } catch (error) {
        self.state = 'error';
        captureException(error);

      }

      return self.techniques;
    });
    const fetchSounds = flow(function* (language: string) {
      self.state = 'pending';
      try {
        const res = yield clientFunction(language).query({ query: GET_BREATHING_SOUNDS });
        if (res) {
          const data = res.data.settingsVariables.items[0];
          self.laura = {
            breatheIn: data.lauraBreatheIn,
            breatheOut: data.lauraBreatheOut,
            hold: data.lauraHold
          };
          self.paul = {
            breatheIn: data.paulBreatheIn,
            breatheOut: data.paulBreatheOut,
            hold: data.paulHold
          };
          self.bell = {
            breatheIn: data.bellBreatheIn,
            breatheOut: data.bellBreatheOut,
            hold: data.bellHold
          };
        }
        self.state = 'done';
      } catch (error) {
        self.state = 'error';
        captureException(error);

      }

      return self.techniques;
    });
    return {
      setTechniqueIndex(techniqueIndex: number) {
        self.techniqueIndex = techniqueIndex;
      },
      setTechniqueId(techniqueId: string) {
        const index = self.techniques?.findIndex(tech => tech._id === techniqueId);
        if (!isUndefined(index) && index !== -1) {
          this.setTechniqueIndex(index);
        }
      },
      setTimerDuration(timerDuration: number) {
        self.timerDuration = timerDuration;
      },
      setStepVibrationFlag(stepVibrationFlag: boolean) {
        self.stepVibrationFlag = stepVibrationFlag;
      },
      setGuidedBreathingMode(guidedBreathingMode: string) {
        self.guidedBreathingMode = guidedBreathingMode;
      },
      toggleTimer() {
        self.timerDuration = self.timerDuration ? 0 : 3 * 1000 * 60;
      },
      toggleStepVibration() {
        self.stepVibrationFlag = !self.stepVibrationFlag;
      },
      fetchTechniques,
      fetchSounds
    };
  })
  .views(self => ({
    get getTechnique() {
      if (self.techniques && self.techniques.length > 0) {
        return self.techniques[self.techniqueIndex];
      } else {
        return null;
      }
    },
    getTechniqueFromId(id :string) {
      const index = self.techniques?.findIndex(tech => tech._id === id);
      if (index !== -1) {
        return self.techniques?.[index as number];
      } else {
        return null;
      }
    },
    get getTechniqueIndex() {
      return Math.round(self.techniqueIndex);
    },
    get getTechniques() {
      return self.techniques?.slice();
    },
    get getTimerDuration() {
      return self.timerDuration;
    },
    getDurationIndex(durations: number[]) {
      const index = durations.findIndex(duration => Math.round(duration) === Math.round(self.timerDuration));
      return Math.min(durations.length-2,Math.max(1,index));
    },
    get getStepVibrationFlag() {
      return self.stepVibrationFlag;
    },
    get getGuidedBreathingMode() {
      return self.guidedBreathingMode;
    },
    soundPath(voice: GuidedBreathingModeEnum, time: BreathingTimeEnum ) {
      let person= null;
      if(String(voice) === 'laura') {
        person = self.laura;
      }
      if(String(voice) === 'paul') {
        person = self.paul;
      }
      if(String(voice) === 'bell') {
        person = self.bell;
      }
      if(!person) return null;
      if(String(time) === 'BreatheIn') {
        return person?.breatheIn;
      }
      if(String(time) === 'BreatheOut') {
        return person?.breatheOut;
      }
      if(String(time) === 'Hold') {
        return person?.hold;
      }
    }
  }));

export type ITechniqueStore = Instance<typeof TechniqueModel>
